/**
 * src/pages/Acceder.jsx
 * Acceso de un cliente. Si viene de una ficha de plan, sigue hacia el alta.
 *
 * Comparte marco con el registro y con /pricing: son las tres pantallas que se
 * ven ANTES de entrar al panel, y hasta el 26-08-2026 cada una se dibujaba a su
 * manera sobre gris claro, contra una landing oscura.
 *
 * Aquí había además dos cosas que decían lo que no era:
 *  · el botón anunciaba «Entrar y PAGAR plan X» cuando con prueba no se cobra;
 *  · el enlace a «Crear cuenta» se dejaba el CICLO por el camino, así que quien
 *    había elegido Anual acababa de alta en mensual sin enterarse.
 */
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { crearCheckout, getMe, loginUser } from '../api/billing'
import { buscarPlan } from '../data/planes'
import { EnlaceSuave, PantallaPublica } from '../components/publico/PantallaPublica'
import { Aviso, Campo } from '../components/publico/Campo'
import { BotonPrincipal } from '../components/publico/BotonPrincipal'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000'

export default function Acceder() {
  const [params] = useSearchParams()
  const tier = params.get('tier')
  // El ciclo elegido en la página de precios debe sobrevivir al login.
  const ciclo = params.get('ciclo') || 'mensual'
  const plan = buscarPlan(tier)

  const [form, setForm] = useState({ email: '', password: '' })
  const [error, setError] = useState('')
  const [cargando, setCargando] = useState(false)
  const setJwt = useAuthStore(state => state.setJwt)
  const navigate = useNavigate()

  function set(campo) {
    return e => {
      const v = e.target.value
      setForm(f => ({ ...f, [campo]: v }))
      if (error) setError('')
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.email || !form.password) { setError('Completa los dos campos.'); return }
    setCargando(true); setError('')
    try {
      const data = await loginUser({ email: form.email, password: form.password })
      setJwt(data.access_token, { email: data.email, nombre: data.nombre, rol: data.rol })
      if (tier) {
        const { url } = await crearCheckout(tier, ciclo)
        window.location.href = url
      } else if (data.rol === 'admin') {
        navigate('/', { replace: true })
      } else {
        // Cliente sin plan preseleccionado: el destino depende de en qué punto del
        // alta está. Con tenant → su dashboard; pagó pero sin tenant → onboarding
        // (no /pricing, que era el bug); sin suscripción → a elegir plan.
        let me = null
        try { me = await getMe() } catch { /* si falla, caemos al default */ }
        if (me?.cliente_slug) {
          window.location.href = `${API_BASE}/data/${me.cliente_slug}/dashboard`
        } else if (me?.tier) {
          navigate('/onboarding', { replace: true })
        } else {
          navigate('/pricing', { replace: true })
        }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Email o contraseña incorrectos.')
      } else {
        setError(err.response?.data?.detail || 'No se pudo iniciar sesión. Inténtalo de nuevo.')
      }
      setCargando(false)
    }
  }

  // El ciclo viaja SIEMPRE con el tier: separarlos era el bug.
  const enlaceRegistro = tier ? `/register?tier=${tier}&ciclo=${ciclo}` : '/register'

  return (
    <PantallaPublica maxAncho={460}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '26px' }}>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', textAlign: 'center' }}>
          <h1 style={{
            margin: 0, fontFamily: 'var(--font-brand)', fontSize: 'var(--text-3xl)',
            fontWeight: 600, letterSpacing: '-0.015em',
          }}>
            Entra en tu panel
          </h1>
          {plan && (
            <p style={{ margin: 0, fontSize: 'var(--text-md)', color: 'var(--color-text-2)' }}>
              Seguimos con el plan{' '}
              <strong style={{ color: 'var(--color-text)', fontWeight: 600 }}>{plan.nombre}</strong>
              {' · '}{ciclo === 'anual' ? 'anual' : 'mensual'}
            </p>
          )}
        </div>

        <div style={{
          background: 'var(--color-surface)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)', padding: '30px',
          boxShadow: 'var(--shadow-lg)',
        }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <Campo etiqueta="Email" tipo="email" name="email" autoComplete="email"
                   placeholder="tu@empresa.cl" valor={form.email} onChange={set('email')}
                   disabled={cargando} autoFocus />

            <Campo etiqueta="Contraseña" tipo="password" name="password"
                   autoComplete="current-password" placeholder="Tu contraseña"
                   valor={form.password} onChange={set('password')} disabled={cargando} />

            <Aviso>{error}</Aviso>

            <BotonPrincipal type="submit" cargando={cargando} textoCargando="Entrando…">
              {plan ? `Entrar y continuar con ${plan.nombre}` : 'Iniciar sesión'}
            </BotonPrincipal>
          </form>

          <div style={{
            display: 'flex', flexDirection: 'column', gap: '9px',
            marginTop: 'var(--space-5)', textAlign: 'center',
          }}>
            <p style={{ margin: 0, fontSize: 'var(--text-md)', color: 'var(--color-text-2)' }}>
              ¿No tienes cuenta? <EnlaceSuave to={enlaceRegistro}>Crear cuenta</EnlaceSuave>
            </p>
            <p style={{ margin: 0, fontSize: 'var(--text-md)' }}>
              <EnlaceSuave to="/forgot-password">¿Olvidaste tu contraseña?</EnlaceSuave>
            </p>
          </div>
        </div>
      </div>
    </PantallaPublica>
  )
}
